import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import useGlobalFilters from "../../hooks/useGlobalFilters";
import {
  BarChartOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  ReloadOutlined,
  SettingOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

// Import sub-module components
import SalesDashboard from "./components/SalesDashboard/SalesDashboard";
import ReportsSidebar from "./components/Reports/ReportsSidebar";
import AccountsPayable from "./components/AccountsPayable/AccountsPayable";
import AccountsReceivable from "./components/AccountsReceivable/AccountsReceivable";
import TransactionsAllocations from "./components/TransactionsAllocations/TransactionsAllocations";
import AdvanceAccounting from "./components/AdvanceAccounting/AdvanceAccounting";
import Settings from "./components/Settings/Settings";
import LetterProcessingRecords from "./components/Shared/Utils/LetterProcessingRecords";
import FinancialReport from "./components/Reports/FinancialReport";

const AccountingSubModules = () => {
  const location = useLocation();
  const [activeKey, setActiveKey] = useState("reports-list");
  const { selectedBrands } = useGlobalFilters();

  // Define the sub-modules with their routes and icons
  const subModules = useMemo(() => [
    {
      key: "sales-dashboard",
      label: "Sales Dashboard",
      path: "/accounting/sales-dashboard",
      icon: <BarChartOutlined />,
      component: SalesDashboard,
    },
    {
      key: "reports-list",
      label: "Reports",
      path: "/accounting/reports-list",
      icon: <BarChartOutlined />,
      component: ReportsSidebar,
    },
    {
      key: "financial-reports",
      label: "Financial Reports",
      path: "/accounting/financial-reports",
      icon: <FileTextOutlined />,
      component: FinancialReport,
    },
    {
      key: "accounts-payable",
      label: "Accounts Payable",
      path: "/accounting/accounts-payable",
      icon: <ArrowDownOutlined />,
      component: AccountsPayable,
    },
    {
      key: "accounts-receivable",
      label: "Accounts Receivable",
      path: "/accounting/accounts-receivable",
      icon: <ArrowUpOutlined />,
      component: AccountsReceivable,
    },
    {
      key: "transactions-allocations",
      label: "Transactions & Allocations",
      path: "/accounting/transactions-allocations",
      icon: <FileTextOutlined />,
      component: TransactionsAllocations,
    },
    {
      key: "advance-accounting",
      label: "Advance Accounting",
      path: "/accounting/advance-accounting",
      icon: <ReloadOutlined />,
      component: AdvanceAccounting,
    },
    {
      key: "letter-processing-records",
      label: "Letter Processing Records",
      path: "/accounting/letter-processing-records",
      icon: <FileTextOutlined />,
      component: LetterProcessingRecords,
    },
    {
      key: "settings",
      label: "Settings",
      path: "/accounting/settings",
      icon: <SettingOutlined />,
      component: Settings,
    },
  ], []);

  // Derive component from URL every render so the correct page always shows (no wrong/blank first paint)
  const pathname = (location.pathname || "").replace(/\/$/, "") || "/";
  const currentModule = useMemo(() => {
    const sorted = [...subModules].sort((a, b) => b.path.length - a.path.length);
    // 1) Exact or prefix match on full path
    let match = sorted.find((m) => pathname === m.path || pathname.startsWith(m.path + "/"));
    if (match) return match;
    // 2) Fallback: match by last path segment (handles different basename e.g. pathname is /accounts-receivable)
    const segment = pathname.split("/").filter(Boolean).pop() || "";
    return sorted.find((m) => m.key === segment) || null;
  }, [pathname, subModules]);
  const CurrentComponent = currentModule ? currentModule.component : ReportsSidebar;

  useEffect(() => {
    if (currentModule) setActiveKey(currentModule.key);
  }, [currentModule?.key]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <CurrentComponent selectedBrands={selectedBrands} />
      </div>
    </div>
  );
};

export default AccountingSubModules;
